import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import TableShimmer from "components/Shared/TableShimmer"
import { pdfDownload, post } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import React, { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Badge, Card, CardBody, Col, Row, Table } from "reactstrap"
import { getUserActivity } from "store/actions/commonAction"
import { parseISO, format } from "date-fns"
import { useTranslation } from "react-i18next"
import { showAlert } from "store/reducers/alertReducer"
import { todayDate } from "components/Common/constants"
import isPermissionEnabled from "common/isPermissionEnabled"
import { getUsersList } from "store/actions/usersAction"

const UserActivity = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("User Activity | Namq")
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const minDate = searchParams.get("minDate") || todayDate
  const maxDate = searchParams.get("maxDate") || todayDate

  const { userRole, accountType, authUser } = useSelector(state => state.Login)
  const { usersList } = useSelector(state => state.Users)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const flatpickrRef = useRef(null)

  const [dateRange, setDateRange] = useState({ min: minDate, max: maxDate })
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState({
    // status: 2,
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
    type: searchParams.get("type") || "",
    userId: null,
  })
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [usersOptions, setUsersOptions] = useState([])

  const {
    userActivityLoading,
    itemsPerPage,
    userActivities,
    userActivityTotalPages,
    userActivityTotalCount,
    userActivityCurrentPage,
  } = useSelector(state => state.Common)

  useEffect(() => {
    if (usersList.length > 0) {
      const updatedCustomerOption = usersList?.map(item => ({
        label: `${item.name ? item.name + "," : ""} ${
          item?.email ? item?.email : ""
        }`,
        value: item._id,
      }))
      setUsersOptions(updatedCustomerOption)
    }
  }, [usersList])

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      36,
      "user_activity_1",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [])

  useEffect(() => {
    fetchUserActivity()
  }, [])

  const fetchUsersList = keyword => {
    const payload = {
      keyword,
    }
    dispatch(getUsersList(payload))
  }

  const onInputChangeSingle = value => {
    value && fetchUsersList(value)
  }

  const fetchUserActivity = () => {
    const { sortOrder, type } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: 2,
      orderBy: sortOrder,
      type: type ? [type] : [],
      referenceId: "",
      userId: "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getUserActivity(payload))
    setIsApiCalled(true)
  }

  const filters = [
    // {
    //   name: "status",
    //   type: "select",
    //   options: [
    //     { label: "All", value: 2 },
    //     { label: "Activate", value: 1 },
    //     { label: "Deactivate", value: 0 },
    //   ],
    //   placeholder: "All",
    // },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Descending"), value: 2 },
        { label: t("Ascending"), value: 1 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "type",
      type: "select",
      options: [
        { label: t("All"), value: "" },
        { label: t("Category"), value: "category" },
        { label: t("Item"), value: "item" },
        { label: t("Modifier Group"), value: "modifierGroup" },
        { label: t("Branch"), value: "branch" },
        { label: t("Loyalty Points"), value: "loyalty-points" },
      ],
      placeholder: t("Select Type"),
    },
    {
      name: "userId",
      type: "singleSelect",
      options: [
        ...usersOptions.map(item => ({
          label: item.label,
          value: item.value,
        })),
      ],
      placeholder: t("Type To Search Users"),
    },
  ]

  const handleFetchUserActivity = (page = 1) => {
    const { sortOrder, type, userId } = selectedFilters
    const searchParams = new URLSearchParams()
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())
    searchParams.set("type", type)
    if (dateRange.min && dateRange.max) {
      searchParams.set("minDate", dateRange.min?.toString())
      searchParams.set("maxDate", dateRange.max?.toString())
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: 2, // Always set status to 2 (all)
      orderBy: Number(sortOrder),
      type: type ? [type] : [],
      referenceId: searchTerm || "", // Use searchTerm or an empty string
      userId: userId?.value ? userId?.value : "", // Always set userId to an empty string
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    dispatch(getUserActivity(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/user-activity?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchUserActivity(1) // Always start the search from page 1
  }

  const onPageChange = page => {
    handleFetchUserActivity(page) // Fetch the selected page
  }

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 2,
      type: [],
      referenceId: "",
      userId: "",
      dateRange: { min: todayDate, max: todayDate },
    }
    navigate(`/user-activity`)
    dispatch(getUserActivity(payload))
    setSelectedFilters({
      // status: 2,
      sortOrder: 2,
      type: "",
      userId:"",
    })
    setSearchTerm("")
    setDateRange({ min: todayDate, max: todayDate })
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate([todayDate, todayDate])
    }
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 500)

  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const handleExcelDownload = async () => {
    const { status, userId } = selectedFilters

    const payload = {
      status: 1,
      keyword: searchTerm || "",
      type: "USER-ACTIVITY",
      exportType: "xlsx",
      userId: userId?.value ? userId?.value : "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    const response = await post(EXPORT_DATA, payload)
    if (response) {
      // const xlsxData = response
      // DownloadXLSX(xlsxData, t("User Activity"))
      const { statusCode, status, msg } = response
      if (statusCode === 200 && status) {
        dispatch(showAlert({ text: msg, type: "success" }))
      } else {
        dispatch(showAlert({ text: msg, type: "error" }))
      }
    }
  }

  const exportToCSV = async () => {
    const { status, userId } = selectedFilters

    const payload = {
      status: 1,
      keyword: searchTerm || "",
      type: "USER-ACTIVITY",
      exportType: "csv",
      userId: userId?.value ? userId?.value : "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    const response = await post(EXPORT_DATA, payload)
    if (response) {
      // const csvData = response
      // DownloadCSV(csvData, t("User Activity"))
      const { statusCode, status, msg } = response
      if (statusCode === 200 && status) {
        dispatch(showAlert({ text: msg, type: "success" }))
      } else {
        dispatch(showAlert({ text: msg, type: "error" }))
      }
    }
  }

  const formatDate = utcDateString => {
    // Parse the UTC date string
    const date = parseISO(utcDateString)

    // Format the date to local time with the desired format
    return format(date, "d MMMM yyyy 'at' h:mm a")
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Users Activities List")}
                isAddBtn={false}
                isXLSX={false}
                isExportData={
                  userActivityLoading ? false : authUser?.allowExports
                }
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
              />
              <CardBody className="overflow-auto">
                <Filters
                  hideSearch={true}
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  isCustomPageSize={true}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                  searchBy={t("Reference Id")}
                  onInputChangeSingle={onInputChangeSingle}
                  colSize={4}
                />
                {userActivityLoading ? (
                  <TableShimmer row={7} col={6} />
                ) : (
                  <Table hover>
                    <thead className="full_th">
                      <tr>
                        <th>{t("S. No.")}</th>
                        <th className="min_w_100">{t("Activity")}</th>
                        <th className="min_w_100">{t("Activity Type")}</th>
                        <th className="min_w_100">{t("Activity On")}</th>
                        <th className="min_w_100">{t("Name")}</th>
                        <th className="min_w_100">{t("Email")}</th>
                        {/* <th className="min_w_100">Status</th> */}
                        {/* <th className="min_w_100">Action</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {userActivities.length > 0 ? (
                        userActivities.map((activity, index) => (
                          <tr
                            key={activity.id}
                            onClick={() =>
                              navigate(
                                `/user-activity/details?id=${activity._id}`
                              )
                            }
                            className="cursor-pointer"
                          >
                            <th scope="row">
                              {(userActivityCurrentPage - 1) * itemsPerPage +
                                index +
                                1}
                            </th>
                            <td>{`${
                              language === "sa"
                                ? activity.messageAr
                                : activity.message
                            } ${t("on")} ${formatDate(
                              activity.createdAt
                            )}`}</td>
                            <td>
                              {activity.actionType === "ADD" && (
                                <Badge className="bg-primary">{t("ADD")}</Badge>
                              )}
                              {activity.actionType === "UPDATE" && (
                                <Badge className="bg-success">
                                  {t("UPDATE")}
                                </Badge>
                              )}
                              {activity.actionType === "DELETE" && (
                                <Badge className="bg-danger">
                                  {t("DELETE")}
                                </Badge>
                              )}
                            </td>
                            <td>
                              {activity.type === "loyalty-points" &&
                                t("Loyalty Points")}
                              {activity.type === "category" && t("Category")}
                              {activity.type === "item" && t("Item")}
                              {activity.type === "modifierGroup" &&
                                t("Modifier Group")}
                              {activity.type === "branch" && t("Branch")}
                            </td>
                            <td>
                              {activity?.user?.firstName || "-"}{" "}
                              {activity?.user?.lastName}
                            </td>
                            <td>{activity?.user?.email || "-"} </td>

                            {/* <td>
                              {activity.status === 1 ? (
                                <Badge className="bg-success">Activate</Badge>
                              ) : (
                                <Badge className="bg-danger">Deactivate</Badge>
                              )}
                            </td> */}
                            {/* <td>
                              <Button
                                color="primary"
                               
                              >
                                Details
                              </Button>
                            </td> */}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center my-3">
                            {t("No user activity found")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}

                {!userActivityLoading && (
                  <div className="mt-3">
                    <Paginations
                      data={userActivities}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={userActivityCurrentPage}
                      totalPages={userActivityTotalPages}
                      totalCount={userActivityTotalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default UserActivity
